import { useState, useEffect } from 'react';
import { Button, Grid, Typography, Divider, TextField, Box, Paper, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getPmorderInfoByProductidAPI, savePmorderInfoAPI } from 'components/pmorder/PmorderAPI';
import { placePMorderAPI } from 'api/OmsOrdermgmtWebServices';
import { updateProductObj } from 'store/slice/PharmaSlice';
import { update } from 'lodash';
import { completeTaskAPI } from '../../../../api/CorePlatformserviceWebServices';
import {initRefreshTaskList} from 'store/slice/AppSlice';

export default function DesignerPlaceMaterialOrder() {
    const dispatch = useDispatch();
    const { productObj } = useSelector(state => state.pharmaslice.productDashboard);
    const {taskList} = useSelector(state => state.appslice.wkflow);

    const [foilKg, setFoilKg] = useState('');
    const [cartonQty, setCartonQty] = useState('');
    const [tubeQty, setTubeQty] = useState('');
    const [bottleQty, setBottleQty] = useState('');
    const [outerCartonQty, setOuterCartonQty] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Local state for pmOrder
    const [pmOrder, setPmOrder] = useState(null);

    // Dialog state
    const [editOpen, setEditOpen] = useState(false);
    const [editPackagingMaterial, setEditPackagingMaterial] = useState('');
    const [editOrderDate, setEditOrderDate] = useState('');

    // Complete task state
    const [completeTaskError, setCompleteTaskError] = useState('');
    const [completeTaskSuccess, setCompleteTaskSuccess] = useState(false);

    useEffect(() => {
        if (productObj?.pmorderid) {
            getPmorderInfoByProductidAPI(productObj.id, (response) => {
                if (response[0]!==undefined && response[0]!==null) {
                    setPmOrder(response[0]);
                    setEditPackagingMaterial(response[0].title || '');
                    // Convert to YYYY-MM-DD for date input
                    const apiDate = response[0].orderdate;
                    setEditOrderDate(apiDate ? apiDate.split('T')[0] : '');
                }
            },
            () => {

            });
        } else {
            setPmOrder(null);
            setEditPackagingMaterial('');
            setEditOrderDate('');
        }
    }, [productObj]);

    // Open dialog and prefill values
    const handleEditClick = () => {
        setEditPackagingMaterial(pmOrder?.title || '');
        // Convert to YYYY-MM-DD for date input
        setEditOrderDate(pmOrder?.orderdate ? pmOrder.orderdate.split('T')[0] : '');
        setEditOpen(true);
    };

    const handleDialogClose = () => {
        setEditOpen(false);
    };

    const handleDialogSave = () => {
        // Ensure order date is in ISO format (YYYY-MM-DD)
        placePMorderAPI({
            productId: productObj.id,
            material: editPackagingMaterial,
            orderDate: editOrderDate
        }, (response) => {
            dispatch(updateProductObj(response.productInfo));
            setPmOrder(response.pmorderInfo);
            setEditOpen(false);
        },
        (error) => {
            console.error("Error saving PM order:", error);
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setSubmitting(true);
        // Submit logic here
        setSubmitting(false);
    };
    const completeTask = () => {
        setCompleteTaskError(''); // Remove error when user clicks again
        setCompleteTaskSuccess(false);
        const taskObj = taskList.find(task => task.boobjid === productObj.id && task.taskstatusid === 2);
        if (!taskObj) {
            setCompleteTaskError("No task instance found for the product.");
            return;
        }
        completeTaskAPI({
            taskInstanceId: taskObj.id,
        }, (response) => {
            setCompleteTaskSuccess(true);
            dispatch(initRefreshTaskList()); // Trigger task list refresh
        }, (error) => {
            setCompleteTaskError(error || "Error completing task.");
        });
    };

    return (
        <Grid container spacing={2} sx={{ position: 'relative', minHeight: '80vh' }}>
            {/* Product Details Column */}
            <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 2, backgroundColor: 'white' }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Product Details
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    {productObj ? (
                        <Box sx={{ borderRadius: 2, p: 0 }}>
                            <Box sx={{ mb: 2, backgroundColor: 'secondary.100', p: 2, borderRadius: 1 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={4}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 100, color: 'text.secondary', fontSize: 14 }}>
                                            Boxe(s)
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 200, color: 'text.primary', fontSize: 15 }}>
                                            {productObj.batchsize}
                                        </Typography>
                                    </Grid>
                                    
                                    <Grid item xs={8}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 100, color: 'text.secondary', fontSize: 14 }}>
                                            Per Box
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 200, color: 'text.primary', fontSize: 15 }}>
                                            {productObj.packagingsize1name} Strips & {productObj.packagingsize2name} {productObj.formname} Per Strip
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 100, color: 'text.secondary', fontSize: 14 }}>
                                            Form
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 200, color: 'text.primary', fontSize: 15 }}>
                                            {productObj.formname}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={8}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 100, color: 'text.secondary', fontSize: 14 }}>
                                            Packaging Material
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 200, color: 'text.primary', fontSize: 15 }}>
                                            {productObj.packagingtypename}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                            <Box sx={{ mb: 2, backgroundColor: 'secondary.100', p: 2, borderRadius: 1 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 100, color: 'text.secondary', fontSize: 14 }}>
                                            Party
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 200, color: 'text.primary', fontSize: 15 }}>
                                            {productObj.partyname}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 100, color: 'text.secondary', fontSize: 14 }}>
                                            Brand
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 200, color: 'text.primary', fontSize: 15 }}>
                                            {productObj.brandname}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 100, color: 'text.secondary', fontSize: 14, mt: 1 }}>
                                            Product Name
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 200, color: 'text.primary', fontSize: 15 }}>
                                            {productObj.title}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No product details available.
                        </Typography>
                    )}
                </Paper>
            </Grid>
            {/* Packaging Material Order Column */}
            <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Order Info
                        </Typography>
                        <EditIcon sx={{ cursor: 'pointer', color: 'primary.main' }} onClick={handleEditClick} />
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ borderRadius: 2, p: 0 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 100, color: 'text.secondary', fontSize: 14 }}>
                                    Packaging Material
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{ fontWeight: 200, color: 'text.primary', fontSize: 15, whiteSpace: 'pre-line' }}
                                >
                                    {pmOrder?.title || '-'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 100, color: 'text.secondary', fontSize: 14 }}>
                                    Order Date
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 200, color: 'text.primary', fontSize: 15 }}>
                                    {pmOrder?.orderdate ? pmOrder.orderdate.split('T')[0] : '-'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Grid>

            {/* Edit Dialog */}
            <Dialog open={editOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
                    Edit Order Information
                    <IconButton edge="end" color="inherit" onClick={handleDialogClose} aria-label="close">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <Box sx={{ mt: 1 }}>
                        <TextField
                            label="Packaging Material Items"
                            multiline
                            minRows={5}
                            maxRows={5}
                            fullWidth
                            value={editPackagingMaterial}
                            onChange={e => setEditPackagingMaterial(e.target.value)}
                            sx={{ mb: 3 }}
                        />
                        <TextField
                            label="Order Date"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={editOrderDate}
                            onChange={e => setEditOrderDate(e.target.value)}
                        />
                    </Box>
                </DialogContent>
                <Divider />
                <DialogActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                    <Button onClick={handleDialogClose} color="secondary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDialogSave}
                        variant="contained"
                        color="primary"
                        disabled={!editPackagingMaterial.trim() || !editOrderDate}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Complete Task Button at bottom right */}
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 32,
                    right: 32,
                    zIndex: 1200,
                    display: { xs: 'none', md: 'block' }
                }}
            >
                {completeTaskError && (
                    <Box sx={{ mb: 2 }}>
                        <Typography color="error" align="center" variant="body2">
                            {completeTaskError}
                        </Typography>
                    </Box>
                )}
                {completeTaskSuccess ? (
                    <Box
                        sx={{
                            backgroundColor: '#e8f5e9',
                            borderRadius: 2,
                            p: 2,
                            textAlign: 'center',
                            boxShadow: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 32, mr: 1 }} />
                        <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
                            Task completed successfully!
                        </Typography>
                    </Box>
                ) : (
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={completeTask}
                        disabled={!pmOrder}
                    >
                        Complete Task
                    </Button>
                )}
            </Box>
        </Grid>
    );
}